import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaw, faXmark, faCamera, faUpload } from '@fortawesome/free-solid-svg-icons';
import NavigationUser from '../../Layouts/Navigation/NavigationUser';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';
import ImageCropDialog from '../../Profile/ImageCropDialog';
import { getCroppedImg } from '../../Profile/cropUtils';

// Etiqueta de tipo de mascota con color
const TipoBadge = ({ tipo }) => {
  const colors = {
    PERRO: 'bg-yellow-100 text-yellow-800',
    GATO: 'bg-purple-100 text-purple-800',
    AVE: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors[tipo] || 'bg-gray-100 text-gray-700'}`}>
      {tipo}
    </span>
  );
};

function UserPets() {
  const [pets, setPets] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingPetIdRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  useAutoDismissSnack(openSnack, setOpenSnack);

  const userId = sessionStorage.getItem('id_usuario');

  useEffect(() => {
    getPets();
  }, []);

  const getPets = () => {
    axios
      .get(`${BASE_URL}/pets/user/${userId}`)
      .then((response) => setPets(response.data))
      .catch((error) => {
        console.error('Error fetching pets:', error);
        setSnackStatus('error');
        setSnackMessage('Error al cargar las mascotas');
        setOpenSnack(true);
      });
  };

  const triggerFileInput = (petId) => {
    pendingPetIdRef.current = petId;
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const petId = pendingPetIdRef.current;
    if (!petId) return;
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    setCropOpen(true);
  };

  const handleUploadPhoto = (petId, file) => {
    setUploadingId(petId);
    const formData = new FormData();
    // Ensure a filename is provided (blobs from crop may not have a name)
    const filename = file && file.name ? file.name : `pet-${Date.now()}.jpg`;
    formData.append('foto', file, filename);
    axios
      .post(`${BASE_URL}/pets/${petId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        setSnackStatus('success');
        setSnackMessage('Foto actualizada correctamente');
        setOpenSnack(true);
        getPets();
      })
      .catch(() => {
        setSnackStatus('error');
        setSnackMessage('Error al subir la foto');
        setOpenSnack(true);
      })
      .finally(() => setUploadingId(null));
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    pendingPetIdRef.current = null;
  };

  const handleCropComplete = async (croppedAreaPixels) => {
    try {
      const croppedBlob = await getCroppedImg(cropSrc, croppedAreaPixels);
      const petId = pendingPetIdRef.current;
      setCropOpen(false);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
      pendingPetIdRef.current = null;
      if (petId) handleUploadPhoto(petId, croppedBlob);
    } catch (error) {
      console.error('Error al recortar imagen:', error);
      setSnackStatus('error');
      setSnackMessage('Error al procesar imagen');
      setOpenSnack(true);
      setCropOpen(false);
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
      pendingPetIdRef.current = null;
    }
  };

  return (
    <>
      <NavigationUser />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Snackbar */}
          {openSnack && (
            <div
              className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
                snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
              } text-white animate-fade-in-down`}
              style={{ zIndex: 9999 }}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold">{snackMessage}</span>
                <button
                  onClick={() => setOpenSnack(false)}
                  className="ml-2 hover:bg-white/20 rounded p-1 transition-colors"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <FontAwesomeIcon icon={faPaw} className="h-10 w-10 text-emerald-600" />
              <h1 className="text-4xl font-bold text-gray-800">Mascotas del Apartamento</h1>
            </div>

            {/* Mensaje informativo */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg shadow-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 font-semibold">ℹ️ Mascotas registradas</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Aquí se muestran todas las mascotas registradas para tu apartamento. Para
                    agregar, modificar o eliminar mascotas, por favor comuníquese directamente con
                    el área de administración del conjunto.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden file input for photo upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />

          {cropOpen && cropSrc && (
            <ImageCropDialog image={cropSrc} onCropComplete={handleCropComplete} onCancel={handleCropCancel} />
          )}

          {/* Pet Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <div
                key={pet.id_mascota}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all overflow-hidden"
              >
                {/* Photo area */}
                <div className="relative bg-gradient-to-br from-emerald-100 to-teal-100 w-full" style={{ paddingTop: '100%' }}>
                  {pet.foto ? (
                    <img
                      src={`${BASE_URL}${pet.foto}`}
                      alt={pet.nombre}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <FontAwesomeIcon icon={faPaw} className="h-20 w-20 text-emerald-300" />
                    </div>
                  )}
                  <button
                    onClick={() => triggerFileInput(pet.id_mascota)}
                    disabled={uploadingId === pet.id_mascota}
                    className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg transition-colors"
                  >
                    {uploadingId === pet.id_mascota ? (
                      <span>Subiendo...</span>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCamera} className="h-3.5 w-3.5" />
                        <span>{pet.foto ? 'Cambiar foto' : 'Subir foto'}</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                        <FontAwesomeIcon icon={faPaw} className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{pet.nombre}</h3>
                        <TipoBadge tipo={pet.tipo} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Raza:</span>
                      <span className="text-sm font-semibold text-gray-800">{pet.raza}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Propietario:</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {pet.usuario_nombre} {pet.usuario_apellido}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Apartamento:</span>
                      <span className="text-sm font-semibold text-gray-800">
                        Apto {pet.num_apartamento || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Torre:</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {pet.torre_nombre || 'N/A'}
                      </span>
                    </div>
                    {pet.telefono && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Teléfono:</span>
                        <span className="text-sm font-semibold text-blue-600">{pet.telefono}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pets.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faPaw} className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No hay mascotas registradas en este apartamento</p>
              <p className="text-sm text-gray-400 mt-2">
                Comunícate con administración para registrar mascotas
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UserPets;
